import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BlurText from '../components/BlurText';
import SEO from '../components/SEO';

const APIReferencePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    useCase: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('API Request submitted:', formData);
    setSubmitted(true);
    setFormData({ name: '', email: '', company: '', useCase: '', message: '' });
  };

  return (
    <div className="api-reference-page">
      <SEO
        title="API Reference — Proxy Management REST API"
        description="PathProxy RESTful API for proxy management. Programmatic access to real-time statistics, instant IP rotation, geo-targeting, and proxy configuration. Request API access."
        path="/api"
      />
      <Navbar />

      <section className="api-hero">
        <div className="container">
          <h1 className="api-title">
            <BlurText text="API Reference" delay={62} direction="top" />
          </h1>
          <p className="api-subtitle">
            <BlurText text="Integrate our proxy services directly into your applications" delay={62} direction="bottom" />
          </p>
        </div>
      </section>

      <section className="api-content">
        <div className="container">
          <div className="api-grid">
            <div className="api-info-section">
              <h2>Powerful API Integration</h2>
              <p>Our API allows you to programmatically manage your proxies, rotate IPs, and monitor usage in real-time.</p>

              <div className="api-features">
                <div className="api-feature">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M8 12L16 4L24 12" stroke="#E8FF00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 20L16 28L24 20" stroke="#E8FF00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div>
                    <h4>RESTful API</h4>
                    <p>Simple and intuitive REST endpoints for easy integration</p>
                  </div>
                </div>

                <div className="api-feature">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <rect x="4" y="8" width="24" height="16" rx="2" stroke="#E8FF00" strokeWidth="2"/>
                    <path d="M8 14H12M8 18H16" stroke="#E8FF00" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <div>
                    <h4>Real-time Statistics</h4>
                    <p>Monitor bandwidth usage and request counts in real-time</p>
                  </div>
                </div>

                <div className="api-feature">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="16" r="12" stroke="#E8FF00" strokeWidth="2"/>
                    <path d="M16 10V16L20 18" stroke="#E8FF00" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <div>
                    <h4>Instant Rotation</h4>
                    <p>Programmatically rotate IPs on demand or on schedule</p>
                  </div>
                </div>

                <div className="api-feature">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M16 4V8M16 24V28M4 16H8M24 16H28" stroke="#E8FF00" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="16" cy="16" r="6" stroke="#E8FF00" strokeWidth="2"/>
                  </svg>
                  <div>
                    <h4>Geo-targeting</h4>
                    <p>Select specific countries or cities for your requests</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="api-contact-section">
              <h2>Request API Access</h2>
              <p>Fill out the form below to get API documentation and credentials.</p>

              {submitted ? (
                <div className="form-success">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="24" r="20" stroke="#E8FF00" strokeWidth="2"/>
                    <path d="M16 24L22 30L32 18" stroke="#E8FF00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>Request Received!</h3>
                  <p>We'll send you the API documentation and credentials within 24 hours.</p>
                  <button className="btn-primary" onClick={() => setSubmitted(false)}>
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name">Your Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">Company (Optional)</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Enter your company name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="useCase">Use Case</label>
                    <select
                      id="useCase"
                      name="useCase"
                      value={formData.useCase}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select your use case</option>
                      <option value="web-scraping">Web Scraping</option>
                      <option value="market-research">Market Research</option>
                      <option value="ad-verification">Ad Verification</option>
                      <option value="seo-monitoring">SEO Monitoring</option>
                      <option value="social-media">Social Media Management</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Additional Details</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us more about your integration needs..."
                      rows="4"
                    ></textarea>
                  </div>

                  <button type="submit" className="btn-primary">
                    Request API Access
                  </button>
                </form>
              )}

              <div className="social-contact">
                <p>Need immediate assistance? Contact us directly:</p>
                <div className="social-buttons">
                  <a href="https://t.me/pathproxy" target="_blank" rel="noopener noreferrer" className="social-btn telegram">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.374 0 0 5.373 0 12s5.374 12 12 12 12-5.373 12-12S18.626 0 12 0zm5.568 8.16c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.499 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.8-1.185-.781-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                    <span>Telegram</span>
                  </a>

                  <a href="https://discord.gg/pathproxy" target="_blank" rel="noopener noreferrer" className="social-btn discord">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    <span>Discord</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default APIReferencePage;
